import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Crown, Coins, LogOut, User as UserIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "../context/AuthContext";
import logoMark from "../assets/logo-mark.png";

const NavLink = ({ to, label, active }) => (
  <Link
    to={to}
    data-testid={`nav-${label.toLowerCase()}`}
    className={`text-sm font-medium tracking-wide transition-colors hover:text-white ${
      active ? "text-white" : "text-slate-400"
    }`}
  >
    {label}
  </Link>
);

export default function Header() {
  const loc = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 glass">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-2.5 group">
          <img src={logoMark} alt="Faceless Studio" className="h-9 w-9 object-contain transition-transform group-hover:scale-105" style={{ filter: "drop-shadow(0 0 10px rgba(255,69,58,0.45))" }} />
          <span className="font-display font-extrabold text-lg tracking-tight leading-none">
            Faceless <span className="text-[#FF6A5E]">Studio</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/" label="Home" active={loc.pathname === "/"} />
          <NavLink to="/studio" label="Create" active={loc.pathname === "/studio"} />
          {user && <NavLink to="/daily" label="Daily" active={loc.pathname === "/daily"} />}
          <NavLink to="/library" label="Library" active={loc.pathname === "/library"} />
          {user && <NavLink to="/connections" label="Connections" active={loc.pathname === "/connections"} />}
          <NavLink to="/analytics" label="Analytics" active={loc.pathname === "/analytics"} />
          <NavLink to="/pricing" label="Pricing" active={loc.pathname === "/pricing"} />
          {user?.role === "admin" && <NavLink to="/admin" label="Admin" active={loc.pathname === "/admin"} />}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              {["pro", "creator_plus"].includes(user.plan) ? (
                <span data-testid="header-plan-badge" className="hidden sm:flex items-center gap-1 rounded-full bg-[#FF453A]/15 px-3 py-1 text-xs font-semibold text-[#FF6A5E]">
                  <Crown className="h-3.5 w-3.5" /> {user.plan_label || (user.plan === "creator_plus" ? "Creator Plus" : "Pro")}
                </span>
              ) : (
                <Link to="/pricing" data-testid="header-credits" className="hidden sm:flex items-center gap-1 rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300 hover:bg-white/10 transition-colors">
                  <Coins className="h-3.5 w-3.5 text-amber-400" /> {user.credits ?? 0}
                </Link>
              )}
              <Button data-testid="header-cta" onClick={() => navigate("/studio")} className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-5 font-semibold transition-colors">
                <Sparkles className="h-4 w-4 mr-1.5" /> New Ad
              </Button>
              <Link to="/account" data-testid="header-account" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E2333] hover:bg-[#262c3d] transition-colors overflow-hidden">
                {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <UserIcon className="h-4 w-4 text-slate-300" />}
              </Link>
              <button data-testid="header-logout" onClick={doLogout} title="Sign out" className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full bg-[#1E2333] text-slate-400 hover:text-white transition-colors">
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="header-login" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Sign in</Link>
              <Button data-testid="header-signup" onClick={() => navigate("/register")} className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-5 font-semibold transition-colors">
                Get started
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
